/**
 * V1 -> V3 Explore query-param translation.
 *
 * coach.kinastic.com historically queried the polymorphic V1 `/coach/explore`
 * surface with params like `rootCategories` / `categories` (slug- or id-based,
 * whole-subtree), `filterTags`, `contentType`, and `sampleSize`. The V3 public
 * endpoints (`/coach/explore/v3/entries` and `/coach/explore/v3/categories`)
 * speak a different, narrower dialect:
 *
 *   - `parentId`      single category UUID (DIRECT children only — no subtree)
 *   - `tags`          dimensional filter set
 *   - `type`          entry-type set
 *   - `page`/`size`/`sort` pagination (size also caps `random`)
 *
 * This helper maps the old shape onto the new one. It only ever sets `parentId`
 * from a value that is an actual category UUID — slug identifiers (e.g.
 * `'mental'`) cannot match V3 entries' UUID `parentIds`, so callers that start
 * from a slug must resolve it to a UUID first (see `exploreCategoryTreeV3`).
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * Pick the parent-category UUID out of a V1 `categories` / `rootCategories`
 * value. Call sites commonly pass `[category.identifier, category.id]` — we want
 * `category.id` (conventionally last). We therefore pick the LAST UUID in the
 * array, which is correct even when the identifier is itself a UUID (company
 * categories use `identifier === gymId`). If no UUID is present (e.g. a bare
 * slug like `['mental']`), we return `undefined` rather than forwarding a slug
 * that would silently match zero V3 rows — the caller is expected to have
 * resolved the slug already.
 */
export function pickParentId(value: unknown): string | undefined {
  if (isUuid(value)) return value;
  if (Array.isArray(value)) {
    const uuids = value.filter((v) => isUuid(v));
    return uuids.length > 0 ? uuids[uuids.length - 1] : undefined;
  }
  return undefined;
}

const DROPPED_KEYS = new Set(['contentType', 'query', 'appVersion', 'system', 'root']);

/**
 * Translate a V1 explore query object into V3 params. Pure and synchronous —
 * slug resolution happens upstream.
 */
export function v1ParamsToV3Params(params?: Record<string, any>): Record<string, any> {
  if (!params) return {};

  const out: Record<string, any> = {};
  const tags: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (DROPPED_KEYS.has(key)) continue;

    switch (key) {
      case 'filterTags':
      case 'tags':
        if (Array.isArray(value)) {
          for (const t of value) if (typeof t === 'string' && t) tags.push(t);
        } else if (typeof value === 'string' && value) {
          tags.push(value);
        }
        break;
      case 'sampleSize':
        out.size = value;
        break;
      case 'categories':
      case 'rootCategories': {
        const parentId = pickParentId(value);
        if (parentId) out.parentId = parentId;
        break;
      }
      default:
        out[key] = value;
    }
  }

  if (tags.length > 0) out.tags = Array.from(new Set(tags));
  return out;
}
